// CF-DEMO-TELEMETRY-V1: per-user demo-play telemetry batcher (frontend).
//
// This is a deliberately Plausible-FREE, identity-FREE, batched beacon. It mirrors the entry
// beacon transport (apiFetch POST /api/v1/demo-flip with Telegram initData auth) but is dispatched
// from local accumulation, NOT once per flip. The backend derives (auth_bot, tg_user_id) from the
// signed initData — this module NEVER sends any identity field; the request body is exactly
// {demoFlips, sessionSecs}.
//
// Design constraints (Scorer scope-agree 2026-06-20T1704Z):
//  - Only DEMO (practice) flips feed this. Real-money flips MUST never call recordDemoFlip().
//  - Batched: flush only after a small count/time threshold, and on visibilitychange/pagehide.
//  - Reset the accumulator AT dispatch. No indefinite retry — prefer losing telemetry over
//    double-counting (a failed flush's deltas are already gone, by design).
//  - Never throw into demo settlement: dispatch is fire-and-forget (caller passes a sender whose
//    rejection is swallowed). This module's pure functions never throw on normal input.
//
// The accumulator + decision logic are pure and unit-tested. The DOM wiring (visibility/pagehide
// listeners, the real apiFetch sender) lives in CoinFlip.tsx, which composes these primitives.

export interface DemoTelemetryState {
  /** Demo flips accumulated since the last dispatch. */
  pendingFlips: number;
  /** Demo session seconds accumulated since the last dispatch. */
  pendingSecs: number;
}

export interface DemoTelemetryBeacon {
  demoFlips: number;
  sessionSecs: number;
}

/** Flush once this many demo flips have accumulated (batched — not per-flip). */
export const DEMO_FLUSH_FLIP_THRESHOLD = 5;
/** Flush once this many accumulated demo session seconds have built up. */
export const DEMO_FLUSH_SECS_THRESHOLD = 120;

export function emptyDemoTelemetryState(): DemoTelemetryState {
  return { pendingFlips: 0, pendingSecs: 0 };
}

/**
 * Add one demo flip plus the session seconds elapsed since the previous demo flip. Returns a NEW
 * state (immutable update). Non-finite / negative / non-integer `deltaSecs` is clamped to 0 so a
 * bad clock reading can never poison the accumulator or get rejected by the server-side validator.
 */
export function accumulateDemoFlip(state: DemoTelemetryState, deltaSecs: number): DemoTelemetryState {
  const safeSecs = Number.isFinite(deltaSecs) && deltaSecs > 0 ? Math.floor(deltaSecs) : 0;
  return {
    pendingFlips: state.pendingFlips + 1,
    pendingSecs: state.pendingSecs + safeSecs,
  };
}

/**
 * Threshold flush decision for the count/time-driven path. True once either threshold is met.
 * Used after each accumulateDemoFlip. (The visibility/pagehide path flushes unconditionally via
 * shouldFlushOnHide.)
 */
export function shouldFlushByThreshold(state: DemoTelemetryState): boolean {
  return state.pendingFlips >= DEMO_FLUSH_FLIP_THRESHOLD || state.pendingSecs >= DEMO_FLUSH_SECS_THRESHOLD;
}

/** Anything pending is worth flushing when the app is being backgrounded/closed. */
export function shouldFlushOnHide(state: DemoTelemetryState): boolean {
  return state.pendingFlips > 0 || state.pendingSecs > 0;
}

/**
 * Build the beacon body from the pending state. ONLY {demoFlips, sessionSecs} — no identity, no
 * choice/outcome/stake/tier/money. Returns null when there is nothing to send (so the caller skips
 * the network call). The returned values are non-negative integers (the accumulator guarantees it).
 */
export function buildDemoBeacon(state: DemoTelemetryState): DemoTelemetryBeacon | null {
  if (state.pendingFlips <= 0 && state.pendingSecs <= 0) return null;
  return { demoFlips: state.pendingFlips, sessionSecs: state.pendingSecs };
}
